import { format, isSameDay } from 'date-fns';
import { toLower } from 'lodash';
import { getSubscriptionTotals as getSharedSubscriptionTotals } from '@repo/lib';
import formatStartDate from '@repo/checkout/lib/formatStartDate';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';

export {
  getRenewalProcessingFeeContribution,
  getSubscriptionCouponMoney as getCouponMoney,
  getSubscriptionVariantRows as getVariantRows,
  hasOrderItems,
} from '@repo/lib';

/** A recurring amount with its interval, as the Subscription card printed every recurring figure. */
export const recurringLabel = (subscription, amount) =>
  getPriceLabel({
    amount,
    currency: subscription.currency,
    interval: subscription.interval,
    intervalCount: subscription.intervalCount,
    type: 'subscription',
  });

export const moneyLabel = (subscription, amount) =>
  getPriceLabel({ amount, currency: subscription.currency });

/** The Subscription card's "Payment type" first line. */
export const getPaymentTypeLabel = subscription =>
  subscription.planIterations
    ? 'Payment plan'
    : subscription.priceSnapshot?.payWhatYouWant
      ? 'Subscription - Pay What You Want'
      : 'Subscription';

/** The Subscription card's "Payment type" second line. */
export const getSubscriptionPeriodStatus = subscription => {
  if (subscription.status === 'canceled') {
    const cancelDate = subscription.cancelAt || subscription.canceledAt || undefined;
    if (cancelDate) return `Canceled on ${format(new Date(cancelDate), 'd MMM, yyyy')}`;
    return 'Canceled';
  }
  if (subscription.cancelAtPeriodEnd)
    return `Ends on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')}`;
  if (subscription.status === 'scheduled')
    return `Starts on ${formatStartDate(subscription.trialEnd)}`;
  if (subscription.status === 'trialing' && subscription.trialEnd)
    return `Trial ends on ${format(new Date(subscription.trialEnd), 'd MMM, yyyy')}`;
  if (subscription.status === 'completed')
    return `Completed on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')}`;
  if (
    subscription.status !== 'completed' &&
    subscription.status !== 'canceled' &&
    isSameDay(new Date(subscription.cancelAt), new Date(subscription.currentPeriodEnd))
  )
    return `Ends on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')}`;
  if (subscription.currentPeriodEnd)
    return `Renews on ${format(new Date(subscription.currentPeriodEnd), 'd MMM, yyyy')}`;
  return '';
};

/** The Subscription card's "No. of payments" row for a payment plan. */
export const getSubscriptionScheduleStatus = subscription => {
  if (!subscription.planIterations) return undefined;
  if (
    subscription.status !== 'completed' &&
    subscription.status !== 'canceled' &&
    subscription.cancelAt
  )
    return `Ends on ${format(new Date(subscription.cancelAt), 'd MMM, yyyy')} after ${subscription.planIterations} payments`;
  return `Ends after ${subscription.planIterations} payments`;
};

export const getCouponDurationLabel = coupon =>
  coupon.duration === 'once'
    ? 'Once'
    : coupon.duration === 'forever'
      ? 'Forever'
      : coupon.duration === 'repeating'
        ? `${coupon.durationInMonths} months`
        : '';

/** The Setup fee row's lines, in the card's wording: the fee, its coupon discount, its fees, its total. */
export const getSetupFeeLines = subscription => {
  if (!subscription.setupFee) return [];
  const money = amount => moneyLabel(subscription, amount);
  const lines = [{ label: 'Setup fee', amount: money(subscription.setupFee) }];
  if (subscription.setupFeeDiscountAmount)
    lines.push({
      label: 'coupon discount',
      amount: `-${money(subscription.setupFeeDiscountAmount)}`,
    });
  (subscription.setupFeeFees ?? []).forEach(fee =>
    lines.push({
      label: `${toLower(fee.name)} (${fee.percentage}%)`,
      amount: `+${money(fee.amount)}`,
    }),
  );
  if (subscription.setupFeeTotal && subscription.setupFee !== subscription.setupFeeTotal)
    lines.push({ label: 'Total:', amount: money(subscription.setupFeeTotal) });
  return lines;
};

/** The subscription's totals, in English, from the shared order summary, closed by its billing lines. */
export const getSubscriptionTotals = subscription =>
  getSharedSubscriptionTotals(subscription, {
    money: amount => moneyLabel(subscription, amount),
    // The Subscription card's billing lines close the ledger (Andy, 3 Oct 2026).
    period: getSubscriptionPeriodStatus,
    schedule: getSubscriptionScheduleStatus,
  });
